import { normalizeBlogSearchText } from "@/lib/blog/search-lexical";

export type BuildBlogSearchQueryArgs = {
  category?: string | string[] | null;
  q?: string | string[] | null;
  page?: number | null;
};

export type BlogSearchQueryState = {
  category: string | null;
  href: string;
  normalizedQuery: string;
  page: number;
  queryLength: number;
  rawQuery: string;
  resultIdentity: string;
  searchIdentity: string;
};

export const BLOG_SEARCH_BROWSE_SIGNATURE = "__browse__";

function normalizeCategory(
  value: string | string[] | null | undefined
): string | null {
  const raw = Array.isArray(value) ? value[0] : value;
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  return trimmed ? trimmed.toLowerCase() : null;
}

function normalizePage(value: number | null | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 1) {
    return 1;
  }
  return Math.trunc(value);
}

function serializeBlogSearchHref({
  category,
  page,
  rawQuery,
}: {
  category: string | null;
  page: number;
  rawQuery: string;
}): string {
  const params = new URLSearchParams();

  if (category) {
    params.set("category", category);
  }
  if (rawQuery) {
    params.set("q", rawQuery);
  }
  if (page > 1) {
    params.set("page", String(page));
  }

  const queryString = params.toString();
  return queryString ? `/blog?${queryString}` : "/blog";
}

export function buildBlogSearchQueryState({
  category,
  q,
  page,
}: BuildBlogSearchQueryArgs = {}): BlogSearchQueryState {
  const normalizedCategory = normalizeCategory(category);
  const rawQ = Array.isArray(q) ? q[0] : q;
  const rawQuery = (rawQ ?? "").trim();
  const normalizedQuery = normalizeBlogSearchText(rawQuery);
  const normalizedPage = normalizePage(page);
  const effectiveRawQuery = normalizedQuery ? rawQuery : "";
  const categoryKey = normalizedCategory ?? "all";
  const searchIdentity = normalizedQuery
    ? [normalizedQuery, categoryKey].join("|")
    : BLOG_SEARCH_BROWSE_SIGNATURE;
  const resultIdentity = normalizedQuery
    ? [normalizedQuery, categoryKey, String(normalizedPage)].join("|")
    : BLOG_SEARCH_BROWSE_SIGNATURE;

  return {
    category: normalizedCategory,
    href: serializeBlogSearchHref({
      category: normalizedCategory,
      page: normalizedPage,
      rawQuery: effectiveRawQuery,
    }),
    normalizedQuery,
    page: normalizedPage,
    queryLength: normalizedQuery.length,
    rawQuery: effectiveRawQuery,
    resultIdentity,
    searchIdentity,
  };
}

export function buildBlogSearchHref(args: BuildBlogSearchQueryArgs = {}): string {
  return buildBlogSearchQueryState(args).href;
}

export function blogSearchQueryLengthFromText(query: string): number {
  return buildBlogSearchQueryState({ q: query }).queryLength;
}

export function blogSearchQueryLengthFromHref(href: string): number {
  const queryString = href.split("?")[1] ?? "";
  if (!queryString) return 0;
  const params = new URLSearchParams(queryString);
  return buildBlogSearchQueryState({
    q: params.get("q"),
  }).queryLength;
}
